import React from "react";
import { A, aa, S, mixHex, onAccent } from "../theme/palette";
import { useTheme, isDarkThemeMode } from "../theme/ThemeProvider";
import { t, tf } from "../i18n";
import { SwapIcon, Spinner } from "./Icons";
import { isCardSelectClick } from "../utils/cardSelect";

// Плитка аккаунта — заменяет строку таблицы (десктоп) и мобильную карточку
// во вкладке «Аккаунты». Всё инлайном, как и остальная панель.
//
// Цвета статуса и рейтинга заданы плотными заливками: прозрачных «стеклянных»
// плашек здесь нет, иначе TOP выбивался бы из ряда.

const STATUS_TONE = {
  online: { label: "Online", text: "#8d93a3", dot: "#6ee7b7" },
  offline: { label: "Offline", text: "#8d93a3", dot: "#6b7080" },
  banned: { label: "Banned", text: "#a98c8c", dot: "#f08f8f" }
};

// TOP, OK и MIX — в цветах акцента темы, поэтому считаются при отрисовке:
// записанные сюда при загрузке модуля, они навсегда оставались индиго. Плашки
// плотные — смесь акцента с подложкой карточки; в тёмном индиго это ровно
// прежние #2c3060 и #242742. В светлой теме подложка белая, а текст — тёмные
// ступени акцента: смесь с тёмной давала на белой карточке тёмно-синие
// пятна. NAJA — предупреждение, у него свой жёлтый в любой теме.
const BADGE_BASE = "#101218";
const badgeTone = (key, light) => {
  if (key === "TOP") return { color: onAccent("#ffffff"), bg: A[500] };
  if (light) {
    if (key === "OK") return { color: A[700], bg: mixHex("#ffffff", A[500], 0.16) };
    if (key === "NAJA") return { color: "#92400e", bg: "#fef3c7" };
    return { color: mixHex(A[700], "#3f4450", 0.5), bg: mixHex("#ffffff", A[500], 0.08) };
  }
  if (key === "OK") return { color: A[200], bg: mixHex(BADGE_BASE, A[500], 0.34) };
  if (key === "NAJA") return { color: "#f3c469", bg: "#3c3222" };
  return { color: mixHex(A[300], "#b0b4be", 0.5), bg: mixHex(BADGE_BASE, A[500], 0.22) };
};

const BADGE_STYLE = { padding: "3px 8px", borderRadius: "7px", fontSize: "10px", fontWeight: 700, letterSpacing: "0.05em", flexShrink: 0 };

// Плашка рейтинга продавца (TOP / OK / MIX / NAJA) — одна на всю панель: её
// же показывает выбор аккаунта в окне объявления.
export const SatisfactionBadge = ({ value = "MIX", style }) => {
  const { mode } = useTheme();
  const tone = badgeTone(value, !isDarkThemeMode(mode));
  return <span style={{ ...BADGE_STYLE, color: tone.color, background: tone.bg, ...style }}>{value}</span>;
};

const PROXY_TONE = {
  ok: { icon: "#6ee7b7", text: "#a5abb8" },
  down: { icon: "#f08f8f", text: "#b7a3a3" },
  none: { icon: "#6b7080", text: "#6b7080" }
};

// Светлая тема: те же смыслы, но цвета темнее — светло-зелёный и
// светло-серый на белой карточке почти не читались.
const PROXY_TONE_LIGHT = {
  ok: { icon: "#059669", text: "#3f4450" },
  down: { icon: "#dc2626", text: "#8a4b4b" },
  none: { icon: "#8d93a3", text: "#707684" }
};

// Метки у аватара — тип аккаунта и свежая регистрация. Раньше это были
// крошечные прозрачные плашки «Gew.» и «Self» в жёлтом и зелёном — чужие в
// карточке, где всё остальное плотное и в цветах темы. Теперь они устроены
// как плашка рейтинга: плотная заливка, смешанная с подложкой карточки,
// значок и полное слово. Gewerblich — в нейтральном с акцентным значком: это
// свойство аккаунта, а не тревога; свежая регистрация остаётся зелёной.
const chipTone = (kind, light) => {
  if (kind === "selfreg") {
    return light
      ? { color: "#047857", bg: "#d8f3e6", icon: "#059669" }
      : { color: "#9be8c6", bg: mixHex(BADGE_BASE, "#10b981", 0.2), icon: "#6ee7b7" };
  }
  return light
    ? { color: "#3f4450", bg: mixHex("#ffffff", A[500], 0.1), icon: A[600] }
    : { color: "#d5d8e0", bg: mixHex(BADGE_BASE, A[500], 0.18), icon: A[300] };
};

const BriefcaseGlyph = ({ color }) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <rect x="3" y="7" width="18" height="13" rx="2.5" />
    <path d="M9 7V5.5A1.5 1.5 0 0 1 10.5 4h3A1.5 1.5 0 0 1 15 5.5V7" />
    <path d="M3 12.5h18" />
  </svg>
);

const SparkGlyph = ({ color }) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="M12 3v4M12 17v4M3 12h4M17 12h4" />
    <path d="m6.3 6.3 2.1 2.1M15.6 15.6l2.1 2.1M6.3 17.7l2.1-2.1M15.6 8.4l2.1-2.1" />
  </svg>
);

// Значок владельца: кто из пользователей панели добавил этот аккаунт.
const OwnerGlyph = ({ color }) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

// shrink — для чипа с текстом произвольной длины (ник владельца). Обычные
// чипы короткие и не сжимаются; такой же несжимаемый ник на телефоне выталкивал
// почту до нуля и вылезал за край плитки. Сжимаемый обрезается многоточием и
// ограничен: в строке почты (small) — 45% строки, чтобы почте осталось место,
// в ряду у аватара — шириной ряда (несколько чипов там и так переносятся).
const TileChip = ({ tone, glyph, title, small = false, shrink = false, children }) => (
  <span
    title={title}
    style={{
      display: "inline-flex", alignItems: "center", gap: "5px",
      height: small ? "20px" : "24px", padding: small ? "0 7px" : "0 9px", borderRadius: small ? "6px" : "8px",
      fontSize: small ? "10.5px" : "11px", fontWeight: 700, letterSpacing: "0.01em",
      color: tone.color, background: tone.bg, whiteSpace: "nowrap",
      flexShrink: shrink ? 1 : 0,
      ...(shrink ? { minWidth: 0, maxWidth: small ? "45%" : "100%" } : {})
    }}
  >
    {glyph}
    {shrink
      ? <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{children}</span>
      : children}
  </span>
);

const LinkGlyph = ({ color }) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="M9 17H7A5 5 0 0 1 7 7h2" />
    <path d="M15 7h2a5 5 0 1 1 0 10h-2" />
    <line x1="8" x2="16" y1="12" y2="12" />
  </svg>
);

const Meter = ({ label, value, extra, pct, fill }) => (
  <div>
    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "8px" }}>
      <span style={{ color: "#6b7080", fontSize: "11px", fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase" }}>{label}</span>
      <span style={{ display: "inline-flex", alignItems: "baseline", gap: "5px", color: "#e2e4e9", fontSize: "13.5px", fontWeight: 700 }}>
        {value}
        {extra}
      </span>
    </div>
    <div style={{ marginTop: "6px", height: "3px", borderRadius: "2px", background: "rgba(141,147,163,0.14)", overflow: "hidden" }}>
      <div style={{ height: "100%", borderRadius: "2px", background: fill, width: `${Math.max(0, Math.min(100, pct))}%`, transition: "width 600ms cubic-bezier(0.22,1,0.36,1)" }} />
    </div>
  </div>
);

export default function AccountTile({
  account,
  metrics = { active: 0, total: 0, chats: 0, unread: 0 },
  displayName,
  hasRealName = true,
  email,
  avatarInitial,
  satisfactionBadge = "MIX",
  statusKey = "offline",
  proxyStateKey = "none",
  followersCount = null,
  registeredAtDisplay = "",
  showSelfreg = false,
  selected = false,
  // Максимумы для шкал считает вкладка (по всем аккаунтам), чтобы полосы были
  // сопоставимы между карточками.
  chatsScale = 1,
  followersScale = 1,
  onToggleSelect,
  onOpenProfile,
  onRefresh,
  // Идёт ли сейчас обновление этого аккаунта: значок крутится всё это время,
  // а не один короткий оборот по нажатию.
  refreshing = false,
  onMenu,
  menuSlot = null,
  // Встроенный прокси Umbrella: город текущего прокси к подписи и кнопка
  // «Сменить» (следующий из пула по кругу). У своих прокси их нет.
  proxyPlace = "",
  onSwapProxy,
  swappingProxy = false,
  // Телефон: имя и почта — справа от аватара, а не строкой под ним, чтобы
  // карточка в ленте была ниже. На ПК плитки стоят сеткой, там прежний вид.
  compact = false
}) {
  const { mode } = useTheme();
  const light = !isDarkThemeMode(mode);
  const tone = STATUS_TONE[statusKey] || STATUS_TONE.offline;
  const proxyTones = light ? PROXY_TONE_LIGHT : PROXY_TONE;
  const proxy = proxyTones[proxyStateKey] || proxyTones.none;
  const typeTone = chipTone("gewerblich", light);
  const selfregTone = chipTone("selfreg", light);
  // Приходит только администратору — см. /api/accounts на сервере.
  const ownerName = String(account?.ownerName || "").trim();
  const noProxyText = t("без прокси");
  const proxyLabelFull = proxyStateKey === "none" ? noProxyText : String(account?.proxy || noProxyText);
  // Сервер отдаёт подпись как «название (хост:порт)». Когда прокси назван своим
  // же адресом, в карточке выходило «103.141.13.38:12666 (103.141.13.38:12666)»
  // — одно и то же дважды, да ещё и с обрезкой. Показываем только название;
  // полная подпись остаётся в подсказке на наведение.
  const proxyName = proxyLabelFull.replace(/\s*\([^()]*\)\s*$/, "").trim() || proxyLabelFull;
  const proxyLabel = proxyPlace ? `${proxyName} · ${proxyPlace}` : proxyName;
  // Тип аккаунта приходит в разном написании: в базе лежит «gewerblich», а
  // после смены имени коммерческого аккаунта бэкенд записывает «COMMERCIAL».
  // Строгое сравнение с одним написанием молча теряло значок на части аккаунтов.
  const accountTypeToken = String(account?.accountType || "").trim().toLowerCase();
  const isGewerblich = accountTypeToken === "gewerblich" || accountTypeToken === "commercial";
  const avatarButton = (
    <button
      type="button"
      className="kl-plain-button"
      onClick={(e) => { e.stopPropagation(); if (onToggleSelect) onToggleSelect(account); }}
      title={t("Выбрать аккаунт")}
      style={{
        position: "relative", width: "56px", height: "56px", borderRadius: "18px", padding: 0,
        background: aa(500, 0.13), border: `1px solid ${selected ? aa(400, 0.6) : aa(500, 0.28)}`,
        color: A[300], display: "flex", alignItems: "center", justifyContent: "center",
        fontFamily: "'Manrope', system-ui, sans-serif", fontSize: "20px", fontWeight: 800,
        cursor: "pointer", flexShrink: 0, transition: "all 0.2s ease"
      }}
    >
      {selected ? (
        <span style={{ position: "absolute", inset: 0, borderRadius: "17px", background: A[500], color: onAccent("#fff"), display: "flex", alignItems: "center", justifyContent: "center" }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
        </span>
      ) : avatarInitial}
    </button>
  );
  const nameLine = (
    <div style={{
      color: hasRealName ? "#f0f1f4" : "#7c8291",
      fontStyle: hasRealName ? "normal" : "italic",
      fontFamily: "'Manrope', system-ui, sans-serif",
      fontSize: "17px", fontWeight: 700, letterSpacing: "-0.015em",
      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"
    }}>{displayName}</div>
  );
  const renderChips = (small) => (
    <>
      {isGewerblich && (
        <TileChip
          small={small}
          tone={typeTone}
          glyph={<BriefcaseGlyph color={typeTone.icon} />}
          title={t("Коммерческий аккаунт: в объявлениях обязателен Impressum")}
        >
          Gewerblich
        </TileChip>
      )}
      {showSelfreg && (
        <TileChip
          small={small}
          tone={selfregTone}
          glyph={<SparkGlyph color={selfregTone.icon} />}
          title={registeredAtDisplay ? tf`Зарегистрирован ${registeredAtDisplay}` : t("Свежая регистрация")}
        >
          Self
        </TileChip>
      )}
      {/* Кто добавил аккаунт. Сервер кладёт ownerName только в ответ
          администратору, поэтому у обычного пользователя чипа нет вовсе. */}
      {ownerName && (
        <TileChip
          small={small}
          shrink
          tone={typeTone}
          glyph={<OwnerGlyph color={typeTone.icon} />}
          title={tf`Аккаунт добавил ${ownerName}`}
        >
          {ownerName}
        </TileChip>
      )}
    </>
  );

  return (
    <div
      className={`account-tile${onToggleSelect ? " is-selectable" : ""}`}
      // Нажатие на пустое место карточки — выбор для массовых действий, как и
      // нажатие на аватар (utils/cardSelect.js).
      onClick={onToggleSelect ? (event) => { if (isCardSelectClick(event)) onToggleSelect(account); } : undefined}
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        borderRadius: "22px",
        border: `1px solid ${selected ? aa(400, 0.45) : "rgba(255,255,255,0.06)"}`,
        // Поверхность из палитры, а не зашитый тёмный цвет: она переключается
        // вместе с темой, поэтому графит подхватывается сам. Светлую тему
        // добирают правила .account-tile в index.css — так устроена вся панель.
        background: S.elevated,
        overflow: "hidden",
        transition: "border-color 220ms ease"
      }}
      onMouseEnter={(e) => { if (!selected) e.currentTarget.style.borderColor = "rgba(141,147,163,0.26)"; }}
      onMouseLeave={(e) => { if (!selected) e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)"; }}
    >
      <div className="account-tile-head" style={{ padding: "18px 18px 14px", display: "flex", flexDirection: "column", gap: "13px", background: `linear-gradient(180deg, ${aa(500, 0.07)}, transparent)` }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {/* Без класса is-online: он вешает пульсацию со свечением, а точка
              должна просто обозначать состояние, а не мигать в каждой карточке. */}
          <span
            className="account-status-indicator-dot"
            style={{ width: "7px", height: "7px", borderRadius: "50%", background: tone.dot, flexShrink: 0 }}
          />
          <span style={{ fontSize: "11px", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: tone.text }}>{tone.label}</span>
          {/* Действия — между состоянием и рейтингом: слева остаётся статус
              аккаунта, справа плашка рейтинга. В подвале они делили строку с
              прокси, и подпись прокси — самое нужное в карточке — обрезалась
              почти всегда. */}
          <div style={{ display: "flex", alignItems: "center", gap: "6px", marginLeft: "auto" }}>
            <button
              type="button"
              className="kl-plain-button"
              onClick={onOpenProfile}
              title={t("Открыть профиль на Kleinanzeigen")}
              style={{ width: "30px", height: "30px", borderRadius: "10px", border: `1px solid ${aa(500, 0.3)}`, background: aa(500, 0.1), color: A[300], display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", padding: 0, flexShrink: 0 }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h6v6" /><path d="M10 14 21 3" /><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /></svg>
            </button>
            <button
              type="button"
              // Пока идёт обновление, «press»-анимация панели не нужна: она
              // делает один короткий оборот по нажатию, а крутиться должно всё
              // время работы.
              className={refreshing ? "kl-plain-button" : "kl-plain-button kl-press-spin"}
              onClick={onRefresh}
              disabled={refreshing}
              title={t("Обновить данные аккаунта")}
              style={{ width: "30px", height: "30px", borderRadius: "10px", border: "1px solid rgba(141,147,163,0.22)", background: "transparent", color: refreshing ? A[300] : "#8d93a3", display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: refreshing ? "wait" : "pointer", padding: 0, flexShrink: 0 }}
            >
              <span className="kl-btn-ico" style={{ display: "inline-flex", animation: refreshing ? "spin 1s linear infinite" : undefined }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" /><path d="M8 16H3v5" /></svg>
              </span>
            </button>
            {menuSlot || (
              <button
                type="button"
                className="kl-plain-button"
                onClick={onMenu}
                title={t("Ещё")}
                style={{ width: "30px", height: "30px", borderRadius: "10px", border: "1px solid transparent", background: "transparent", color: "#707684", display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", padding: 0, flexShrink: 0 }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="12" cy="19" r="2" /></svg>
              </button>
            )}
          </div>
          <SatisfactionBadge value={satisfactionBadge} />
        </div>

        {compact ? (
          /* Телефон: имя и почта справа от аватара; метки — в строке почты,
             отдельной строкой они снова вытянули бы карточку. */
          <div style={{ display: "flex", alignItems: "center", gap: "14px", minWidth: 0 }}>
            {avatarButton}
            <div style={{ minWidth: 0, flex: 1 }}>
              {nameLine}
              <div style={{ marginTop: "4px", display: "flex", alignItems: "center", gap: "6px", minWidth: 0 }}>
                <span title={email} style={{ flex: 1, minWidth: 0, color: "#7c8291", fontSize: "12px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{email}</span>
                {renderChips(true)}
              </div>
            </div>
          </div>
        ) : (
          <>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
              {avatarButton}

              {/* Тип аккаунта и свежая регистрация — рядом с аватаром. */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", flexWrap: "wrap", gap: "6px", minWidth: 0 }}>
                {renderChips(false)}
              </div>
            </div>

            <div style={{ minWidth: 0 }}>
              {nameLine}
              <div style={{ marginTop: "3px", color: "#7c8291", fontSize: "12px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{email}</div>
            </div>
          </>
        )}
      </div>

      <div style={{ height: "1px", background: "rgba(141,147,163,0.1)" }} />

      <div style={{ padding: "15px 18px", display: "flex", flexDirection: "column", gap: "12px" }}>
        <Meter
          label={t("Объявления")}
          value={metrics.active}
          extra={<span style={{ color: "#565c69", fontSize: "11.5px", fontWeight: 600 }}>{` / ${metrics.total}`}</span>}
          pct={metrics.total ? (metrics.active / metrics.total) * 100 : 0}
          fill={A[500]}
        />
        <Meter
          label={t("Чаты")}
          value={metrics.chats}
          extra={metrics.unread > 0 ? (
            <span style={{ fontSize: "10px", fontWeight: 700, color: onAccent("#fff"), background: A[500], borderRadius: "9999px", padding: "1px 6px" }}>{metrics.unread}</span>
          ) : null}
          pct={chatsScale ? (metrics.chats / chatsScale) * 100 : 0}
          fill={A[400]}
        />
        <Meter
          label={t("Фолловеры")}
          value={followersCount !== null ? followersCount : "—"}
          pct={followersScale && followersCount !== null ? (followersCount / followersScale) * 100 : 0}
          fill="rgba(141,147,163,0.55)"
        />
      </div>

      {/* Подвал отдан прокси целиком: это то, что чаще всего нужно увидеть в
          карточке, а раньше подпись делила строку с тремя кнопками и обрезалась
          до многоточия почти всегда. */}
      <div style={{ marginTop: "auto", padding: "12px 18px 14px", display: "flex", alignItems: "center", gap: "10px", borderTop: "1px solid rgba(141,147,163,0.1)" }}>
        <span style={{ minWidth: 0, flex: 1, display: "inline-flex", alignItems: "center", gap: "7px" }}>
          <LinkGlyph color={proxy.icon} />
          {/* Когда прокси нет, сервер присылает подпись «Нет» — показываем
              единообразное «без прокси», а не это слово. */}
          <span title={proxyLabelFull} style={{ minWidth: 0, fontSize: "11.5px", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: proxy.text }}>{proxyLabel}</span>
          {onSwapProxy && (
            <button
              type="button"
              className="account-proxy-swap"
              onClick={(event) => { event.stopPropagation(); if (!swappingProxy) onSwapProxy(account); }}
              disabled={swappingProxy}
              title={t("Сменить прокси Umbrella на следующий")}
              aria-label={t("Сменить прокси Umbrella на следующий")}
            >
              {swappingProxy ? <Spinner size={13} /> : <SwapIcon size={13} />}
            </button>
          )}
        </span>
        <span style={{ color: "#565c69", fontSize: "11px", flexShrink: 0 }}>{registeredAtDisplay || "—"}</span>
      </div>
    </div>
  );
}
